import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { brl, formatDate, statusLabel, statusColor } from "@/lib/format";
import { User, Package, LogOut } from "lucide-react";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user, loading, nav]);

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="font-serif text-3xl text-[#4A3B40] mb-6">Olá, {user.name.split(" ")[0]} 💗</h1>
      <div className="grid md:grid-cols-4 gap-5">
        <aside className="bg-white rounded-2xl border border-[#F9E1E8] p-4 h-fit">
          <div className="w-16 h-16 rounded-full bg-[#F9E1E8] text-[#C9708B] flex items-center justify-center text-2xl font-bold mb-3">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <p className="font-semibold text-[#4A3B40]">{user.name}</p>
          <p className="text-xs text-[#8C7A80] mb-4">{user.email}</p>
          <nav className="space-y-1 text-sm">
            <button onClick={()=>setTab("orders")} className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 ${tab==="orders"?"bg-[#F9E1E8] text-[#C9708B] font-semibold":"text-[#4A3B40] hover:bg-[#FFF9FA]"}`} data-testid="tab-orders">
              <Package className="w-4 h-4" /> Meus pedidos
            </button>
            <button onClick={()=>setTab("profile")} className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 ${tab==="profile"?"bg-[#F9E1E8] text-[#C9708B] font-semibold":"text-[#4A3B40] hover:bg-[#FFF9FA]"}`} data-testid="tab-profile">
              <User className="w-4 h-4" /> Dados pessoais
            </button>
            <button onClick={()=>{logout(); nav("/");}} className="w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-[#E57373] hover:bg-rose-50" data-testid="logout-button">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </nav>
        </aside>

        <main className="md:col-span-3">
          {tab === "orders" ? (
            <div className="space-y-3">
              <h2 className="font-serif text-xl text-[#4A3B40]">Meus pedidos</h2>
              {orders.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#F9E1E8] p-10 text-center text-[#8C7A80]">
                  Você ainda não fez pedidos. <Link to="/" className="text-[#C9708B] font-semibold">Começar a comprar →</Link>
                </div>
              ) : orders.map((o) => (
                <Link key={o.id} to={`/pedido/${o.id}/confirmacao`} className="block bg-white rounded-2xl border border-[#F9E1E8] p-4 hover:shadow-md transition-shadow" data-testid={`order-row-${o.order_number}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-[#4A3B40]">Pedido {o.order_number}</p>
                      <p className="text-xs text-[#8C7A80]">{formatDate(o.created_at)} • {o.items.length} item(s)</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full border ${statusColor(o.status)}`}>{statusLabel(o.status)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {o.items.slice(0,4).map((it,i)=><img key={i} src={it.image} className="w-10 h-10 rounded-full border-2 border-white object-cover" alt="" />)}
                    </div>
                    <p className="font-bold text-[#C9708B]">{brl(o.total)}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#F9E1E8] p-6">
              <h2 className="font-serif text-xl text-[#4A3B40] mb-4">Dados pessoais</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-[#8C7A80]">Nome</p><p className="font-semibold">{user.name}</p></div>
                <div><p className="text-[#8C7A80]">E-mail</p><p className="font-semibold">{user.email}</p></div>
                <div><p className="text-[#8C7A80]">Telefone</p><p className="font-semibold">{user.phone || "—"}</p></div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
